import AppFormSelect from '@/components/App/AppFormSelect';
import NtaDatePicker from '@/components/Form/NtaDatePicker';
import { BookingStatus, PaymentStatus } from '@/constants';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { motion } from 'framer-motion';
import { isNumber } from 'lodash';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type Props = {
  onSearch: (value: any) => void;
};
const getInitSearchValue = () => {
  let otherParams = {};
  const query = new URLSearchParams(window.location.search);
  const autoCancelTimeString = query.get('autoCancelTime');
  let autoCancelTime = parseInt(autoCancelTimeString || '');
  if (autoCancelTime && isNumber(autoCancelTime)) {
    otherParams = {
      autoCancelTime: autoCancelTime,
    };
  }
  return otherParams;
};

const autoCancelTimeOptions = [
  { value: 24, label: '24h' },
  { value: 48, label: '48h' },
];
export default function SearchForm({ onSearch }: Props) {
  const [displayAdvanceSearch, setDisplayAdvanceSearch] = React.useState<boolean>(false);
  const { register, handleSubmit, reset, control } = useForm({ defaultValues: getInitSearchValue() as any });
  const { t } = useTranslation();

  return (
    <form className="y-gap-10 mb-10" onSubmit={handleSubmit(onSearch)}>
      <div className="row  x-gap-5">
        <div className="col-2">
          <input
            defaultValue=""
            title={t('agentBookingListPage_RefNumber') as any}
            {...register('refNumber')}
            className="form-control omega-input"
            placeholder={t('agentBookingListPage_RefNumber') as any}
          />
        </div>
        <div className="col-2">
          <input
            title={t('agentBookingListPage_OrderId') as any}
            {...register('orderId')}
            className="form-control omega-input"
            placeholder={t('agentBookingListPage_OrderId') as any}
          />
        </div>
        <div className="col-2">
          <Controller
            control={control}
            render={({ field: { onChange, onBlur, value } }) => (
              <NtaDatePicker value={value} onBlur={onBlur} onChange={onChange} placeholder={t('agentBookingListPage_BookingFrom') as any} />
            )}
            name="bookingFrom"
          />
        </div>

        <div className="col-2">
          <Controller
            control={control}
            render={({ field: { onChange, onBlur, value } }) => (
              <NtaDatePicker value={value} onBlur={onBlur} onChange={onChange} placeholder={t('agentBookingListPage_BookingTo') as any} />
            )}
            name="bookingTo"
          />
        </div>

        <div className="col d-flex justify-content-between">
          <div className="d-flex">
            <button type="submit" className="button py-10  bg-blue-1 text-white  px-10">
              <i className="icon-search mr-5" /> {t('base.Search')}
            </button>
            <button type="button" className="button py-10  bg-red-2 text-white  px-10 ml-5" onClick={() => reset()}>
              <i className="icon-close text-12 mr-5" /> {t('base.Reset')}
            </button>
          </div>

          <button type="button" className="button py-10  bg-light-1 text-white  px-10 ml-5" onClick={() => setDisplayAdvanceSearch(t => !t)}>
            <FontAwesomeIcon icon={displayAdvanceSearch ? faChevronUp : faChevronDown} className=" text-12 mr-5" />
            {t('agentBookingListPage_AdvancedSearch')}
          </button>
        </div>
      </div>
      {displayAdvanceSearch ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{
            duration: 1,
            delay: 0.1,
            ease: [0, 0.71, 0.2, 1],
          }}
          className="row x-gap-5 y-gap-10"
        >
          <div className="col-2">
            <div className="text-light-1 text-14">{t('agentBookingListPage_CheckinFrom')}</div>
            <Controller
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <NtaDatePicker value={value} onBlur={onBlur} onChange={onChange} placeholder={t('agentBookingListPage_CheckinFrom') as any} />
              )}
              name="checkinFrom"
            />
          </div>
          <div className="col-2">
            <div className="text-light-1 text-14">{t('agentBookingListPage_CheckinTo')}</div>

            <Controller
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <NtaDatePicker value={value} onBlur={onBlur} onChange={onChange} placeholder={t('agentBookingListPage_CheckinTo') as any} />
              )}
              name="checkinTo"
            />
          </div>

          <div className="col-2">
            <div className="text-light-1 text-14">{t('agentBookingListPage_HotelName')}</div>
            <input
              defaultValue=""
              {...register('hotelName')}
              className="form-control omega-input"
              placeholder={t('agentBookingListPage_HotelName') as any}
            />
          </div>
          <div className="col-2">
            <div className="text-light-1 text-14">{t('agentBookingListPage_LeadPassenger')}</div>

            <input
              defaultValue=""
              title={t('agentBookingListPage_LeadPassenger') as any}
              {...register('leadPassenger')}
              className="form-control omega-input"
              placeholder={t('agentBookingListPage_LeadPassenger') as any}
            />
          </div>
          <div className="col-2">
            <div className="text-light-1 text-14">{t('agentBookingListPage_SupplierRefNumbers')}</div>
            <input
              defaultValue=""
              title={t('agentBookingListPage_SupplierRefNumbers') as any}
              {...register('supplierRefNumber')}
              className="form-control omega-input"
              placeholder={t('agentBookingListPage_SupplierRefNumbers') as any}
            />
          </div>

          <div className="col-2">
            <div className="text-light-1 text-14">{t('agentBookingListPage_AgentBookingRef')}</div>

            <input
              defaultValue=""
              title={t('agentBookingListPage_AgentBookingRef') as any}
              {...register('agentBookingRef')}
              className="form-control omega-input"
              placeholder={t('agentBookingListPage_AgentBookingRef') as any}
            />
          </div>

          <div className="col-2">
            <div className="text-light-1 text-14">Auto cancel time</div>

            <Controller
              control={control}
              defaultValue={null}
              render={({ field }) => (
                <AppFormSelect
                  isClearable
                  defaultValue={null}
                  isSearchable={false}
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  classNamePrefix="agent-dashboard-select"
                  className="w-100"
                  options={autoCancelTimeOptions}
                  {...field}
                  placeholder="Auto cancel time"
                />
              )}
              name="autoCancelTime"
            />
          </div>
        </motion.div>
      ) : null}
    </form>
  );
}
